import React from "react"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useLocation, useNavigate } from "react-router"

import { BillDocumentSection } from "@/components/bill-document-section/bill-document-section"
import { BillLineItemsDisplay } from "@/components/bills/bill-line-items-display"
import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import {
  BillHistorySection,
  BillReadonlyFields,
  useAdminBillPageState,
  useConfirmDeleteBill,
} from "@/modules/admin/bills"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import { DataFreshnessAlert } from "@/nzds/feedback/data-freshness-alert"
import { useDisclosure } from "@/nzds/utils"
import { useMeters } from "@/services/meter"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

interface AdminBillLocationState {
  showDataFreshness?: boolean
}

export const AdminBillRoute: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminBillEditPath, adminBillsPath } = useUrl()
  const {
    close: closeFreshnessAlert,
    isOpen: isFreshnessAlertOpen,
    open: openFreshnessAlert,
  } = useDisclosure()

  const { billData, billId, billIsFetched, billIsLoading, billIsUnavailable } =
    useAdminBillPageState()

  const organizationId = activeOrganizationId ?? ""

  const meterId = billData?.meter_id
  const {
    metersData,
    metersIsFetched,
    metersIsLoading,
    metersIsPlaceholderData,
  } = useMeters({
    apiQueryOptions: {
      includes: ["site"],
      pageSize: 1,
      where: {
        id: meterId !== undefined ? [meterId] : [],
        organization_id: organizationId,
      },
    },
    queryOptions: {
      enabled: Boolean(organizationId) && meterId !== undefined,
    },
  })

  const billMeter = metersIsPlaceholderData ? undefined : metersData?.[0]

  const { confirmAndDeleteBill, deleteBillIsPending } = useConfirmDeleteBill({
    organizationId,
    onSuccess: () => {
      void navigate(adminBillsPath, { replace: true })
    },
  })

  React.useEffect(() => {
    const state = location.state as AdminBillLocationState | null
    if (state?.showDataFreshness) {
      openFreshnessAlert()
      void navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      })
    }
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    openFreshnessAlert,
  ])

  if (!organizationId) {
    return null
  }

  if (billIsUnavailable) {
    return <Page404 />
  }

  const isMeterLookupPending =
    billData != null &&
    (metersIsLoading || metersIsPlaceholderData || !metersIsFetched)

  const isLoading =
    billIsLoading ||
    (!billIsFetched && billId !== undefined) ||
    isMeterLookupPending

  const handleDeleteClick = () => {
    if (!billData || billData.is_locked) {
      return
    }

    confirmAndDeleteBill(billData.id).catch(() => undefined)
  }

  const billFieldsContent =
    isLoading || !billData ? (
      <AdminEntityFieldsSkeleton
        ariaLabel={t("loadingAriaLabel", {
          title: t("admin.billDetail.title"),
        })}
      />
    ) : (
      <BillReadonlyFields
        bill={billData}
        meterCode={billMeter?.meterCode}
        meterId={billData.meter_id != null ? String(billData.meter_id) : null}
        siteId={billMeter?.site?.id ?? null}
        siteName={billMeter?.site?.name}
      />
    )

  return (
    <Stack sx={{ gap: 2 }}>
      <PageTitle
        breadcrumbActions={
          <MuiLink component={Link} to={`${adminBillsPath}${location.search}`}>
            {t("admin.bills")}
          </MuiLink>
        }
        heading={t("admin.billDetail.title")}
        primaryActions={
          <Button
            component={NavLink}
            to={`${adminBillEditPath(String(billId))}${location.search}`}
            variant="contained"
          >
            {t("button.edit")}
          </Button>
        }
        secondaryActions={
          <Button
            color="error"
            disabled={deleteBillIsPending || isLoading || billData?.is_locked}
            onClick={handleDeleteClick}
          >
            {t("button.delete")}
          </Button>
        }
      />
      <PageCard>
        <Stack sx={{ gap: 2 }}>
          <DataFreshnessAlert
            onClose={closeFreshnessAlert}
            open={isFreshnessAlertOpen}
          />
          <Stack sx={{ gap: 4 }}>
            {billFieldsContent}
            {!isLoading && billData ? (
              <BillLineItemsDisplay
                billId={billData.id}
                organizationId={organizationId}
              />
            ) : null}
            <BillDocumentSection
              bill={billData ?? null}
              isLoading={isLoading}
            />
            {!isLoading && billData ? (
              <BillHistorySection
                billDetails={billData}
                billId={billData.id}
                organizationId={organizationId}
              />
            ) : null}
          </Stack>
        </Stack>
      </PageCard>
    </Stack>
  )
}
